import {
  MoralisMarketDataTokenInterface,
  TokenInterface,
  WalletTokenInterface,
} from '@/interfaces';
import { generateTokenId } from '@/utils/functionalUtils';
import { logError, logInfo } from '@/utils/logger';
import axios from 'axios';
import axiosRateLimit from 'axios-rate-limit';
const http = axiosRateLimit(axios.create(), {
  maxRequests: 1,
  perMilliseconds: 1000,
});
const CACHE_EXPIRY_TIME = 24 * 60 * 60 * 1000;
const generateCacheKey = (
  walletAddress: string,
  blockchains: string[],
  endpoint: string,
): string => {
  return `${walletAddress}-${blockchains.join(',')}-${endpoint}`;
};
const setCacheWithExpiry = (key: string, data: any) => {
  const now = new Date();
  const item = {
    data,
    expiry: now.getTime() + CACHE_EXPIRY_TIME,
  };
  localStorage.setItem(key, JSON.stringify(item));
};
const getCacheWithExpiry = (key: string): any | null => {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) {
    return null;
  }
  const item = JSON.parse(itemStr);
  const now = new Date();
  if (now.getTime() > item.expiry) {
    localStorage.removeItem(key);
    return null;
  }
  return item.data;
};
const mapBlockchainToMoralisChain = (
  blockchain: string,
): string | undefined => {
  switch (blockchain.toUpperCase()) {
    case 'MATIC-AMOY':
      return 'polygon amoy';
    case 'ETH-SEPOLIA':
      return 'sepolia';
    default:
      return undefined;
  }
};
export const fetchTokenBalancesWithPricesFromMoralis = async (
  walletAddress: string,
  blockchain: string,
): Promise<WalletTokenInterface[] | undefined> => {
  const cacheKey = generateCacheKey(walletAddress, [blockchain], 'tokens');
  const cachedData = getCacheWithExpiry(cacheKey);
  if (cachedData) {
    logInfo(
      'Using cached data for token balances',
      'MoralisService.fetchTokenBalancesWithPricesFromMoralis',
      { cachedData },
    );
    return cachedData;
  }
  try {
    const moralisChain = mapBlockchainToMoralisChain(blockchain);
    if (moralisChain) {
      const response = await http.get(
        `https://deep-index.moralis.io/api/v2.2/wallets/${walletAddress}/tokens`,
        {
          headers: {
            'X-API-Key': process.env.NEXT_PUBLIC_MORALIS_API_KEY,
          },
          params: {
            chain: moralisChain,
          },
        },
      );
      const tokenBalances = response.data.result as WalletTokenInterface[];
      setCacheWithExpiry(cacheKey, tokenBalances);
      return tokenBalances;
    }
  } catch (error) {
    logError(
      'Failed to fetch token balances with prices from Moralis',
      'MoralisService.fetchTokenBalancesWithPricesFromMoralis',
      { walletAddress, blockchain, error },
    );
  }
  return undefined;
};
export const fetchSingleTokenPriceFromMoralis = async (
  tokenAddress: string,
  chain: string,
  includePercentChange: boolean = false,
): Promise<any> => {
  const cacheKey = generateCacheKey(
    tokenAddress,
    [chain],
    'single-token-price',
  );
  const cachedData = getCacheWithExpiry(cacheKey);
  if (cachedData) {
    logInfo(
      'Using cached data for single token price',
      'MoralisService.fetchSingleTokenPriceFromMoralis',
      { cachedData },
    );
    return cachedData;
  }
  try {
    const response = await http.get(
      `https://deep-index.moralis.io/api/v2/erc20/${tokenAddress}/price`,
      {
        headers: {
          'X-API-Key': process.env.NEXT_PUBLIC_MORALIS_API_KEY,
        },
        params: {
          chain,
          include: includePercentChange ? 'percent_change' : undefined,
        },
      },
    );
    setCacheWithExpiry(cacheKey, response.data);
    return response.data;
  } catch (error) {
    logError(
      'Failed to fetch single token price from Moralis',
      'MoralisService.fetchSingleTokenPriceFromMoralis',
      { tokenAddress, chain, includePercentChange, error },
    );
    const emptyData = {};
    setCacheWithExpiry(cacheKey, emptyData);
    return emptyData;
  }
};
export const fetchWalletNetWorthFromMoralis = async (
  walletAddress: string,
  blockchains: string[],
  excludeSpam: boolean = true,
  excludeUnverifiedContracts: boolean = true,
): Promise<any> => {
  const moralisChains = blockchains
    .map(mapBlockchainToMoralisChain)
    .filter((chain): chain is string => chain !== undefined);
  if (moralisChains.length === 0) {
    logError(
      'No valid Moralis chains found for the provided blockchains',
      'MoralisService.fetchWalletNetWorthFromMoralis',
      { walletAddress, blockchains },
    );
    return undefined;
  }
  const cacheKey = generateCacheKey(walletAddress, moralisChains, 'net-worth');
  const cachedData = getCacheWithExpiry(cacheKey);
  if (cachedData) {
    return cachedData;
  }
  try {
    const response = await http.get(
      `https://deep-index.moralis.io/api/v2.2/wallets/${walletAddress}/net-worth`,
      {
        headers: {
          'X-API-Key': process.env.NEXT_PUBLIC_MORALIS_API_KEY,
        },
        params: {
          chains: moralisChains.join(','),
          exclude_spam: excludeSpam,
          exclude_unverified_contracts: excludeUnverifiedContracts,
        },
      },
    );
    const netWorthDetails = response.data;
    setCacheWithExpiry(cacheKey, netWorthDetails);
    return netWorthDetails;
  } catch (error) {
    logError(
      'Failed to fetch wallet net worth from Moralis',
      'MoralisService.fetchWalletNetWorthFromMoralis',
      { walletAddress, blockchains, error },
    );
    return undefined;
  }
};
const testnetToMainnetTokenMapping = [
  {
    testnetToken: '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE',
    testnetTokenSymbol: 'MATIC',
    testnetChain: 'MATIC-AMOY',
    mainnetToken: '0x7d1afa7b718fb893db30a3abc0cfc608aacfebb0',
    mainnetChain: 'eth',
  },
  {
    testnetToken: '0x0fd9e8d3af1aaee056eb9e802c3a762a667b1904',
    testnetTokenSymbol: 'LINK',
    testnetChain: 'MATIC-AMOY',
    mainnetToken: '0x53e0bca35ec356bd5dddfebbd1fc0fd03fabad39',
    mainnetChain: 'polygon',
  },
  {
    testnetToken: '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE',
    testnetTokenSymbol: 'ETH',
    testnetChain: 'ETH-SEPOLIA',
    mainnetToken: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2',
    mainnetChain: 'eth',
  },
  {
    testnetToken: '0x779877a7b0d9e8603169ddbd7836e478b4624789',
    testnetTokenSymbol: 'LINK',
    testnetChain: 'ETH-SEPOLIA',
    mainnetToken: '0x514910771af9ca656af840dff83e8264ecf986ca',
    mainnetChain: 'eth',
  },
  {
    testnetToken: '0x1c7d4b196cb0c7b01d743fbc6116a902379c7238',
    testnetTokenSymbol: 'USDC',
    testnetChain: 'ETH-SEPOLIA',
    mainnetToken: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
    mainnetChain: 'eth',
  },
  {
    testnetToken: '0x08210f9170f89ab7658f0b5e3ff39b0e03c594d4',
    testnetTokenSymbol: 'EURC',
    testnetChain: 'ETH-SEPOLIA',
    mainnetToken: '0x1abaea1f7c830bd89acc67ec4af516284b1bc33c',
    mainnetChain: 'eth',
  },
];
export const fetchEnrichedWalletsUsingMoralis = async (
  wallets: any[],
): Promise<any[]> => {
  const enrichedWallets: any[] = await Promise.all(
    wallets
      .filter((wallet) => wallet.address && wallet.blockchain)
      .map(async (wallet) => {
        let tokenBalances = await fetchTokenBalancesWithPricesFromMoralis(
          wallet.address as string,
          wallet.blockchain as string,
        );
        const mainnetPrices = await Promise.all(
          tokenBalances?.map(async (token: any) => {
            const mapping = testnetToMainnetTokenMapping.find(
              (map) =>
                map.testnetToken === token['token_address'] &&
                map.testnetTokenSymbol === token['symbol'] &&
                map.testnetChain === wallet.blockchain,
            );
            if (mapping) {
              const priceData = await fetchSingleTokenPriceFromMoralis(
                mapping.mainnetToken,
                mapping.mainnetChain,
              );
              return {
                ...token,
                usd_price: priceData?.priceInUsd || 0,
              };
            }
            return token;
          }) || [],
        );
        tokenBalances = mainnetPrices.map((token: any) => {
          const usdPrice = token['usd_price'] || 0;
          const usdValue = usdPrice
            ? parseFloat(token['balance_formatted'] || '0') * usdPrice
            : 0;
          return {
            ...token,
            usd_price: usdPrice,
            usd_value: usdValue,
          };
        });
        const walletValueInUsd = tokenBalances.reduce(
          (total: number, token: any) => total + (token['usd_value'] || 0),
          0,
        );
        return {
          address: wallet.address as string,
          blockchain: wallet.blockchain as string,
          tokens: tokenBalances,
          walletValueInUsd,
        };
      }),
  );
  return enrichedWallets;
};
export const fetchGlobalMarketData = async (): Promise<
  MoralisMarketDataTokenInterface[]
> => {
  try {
    const response = await http.get(
      'https://deep-index.moralis.io/api/v2.2/market-data/global/market-cap',
      {
        headers: {
          'X-API-Key': process.env.NEXT_PUBLIC_MORALIS_API_KEY,
        },
      },
    );
    const processedData: MoralisMarketDataTokenInterface[] = response.data;
    return processedData;
  } catch (error) {
    logError(
      'Failed to fetch global market data from Moralis',
      'MoralisService.fetchGlobalMarketData',
      { error },
    );
    return [];
  }
};
export const fetchTopErc20TokensData = async (): Promise<TokenInterface[]> => {
  try {
    const response = await http.get(
      'https://deep-index.moralis.io/api/v2.2/market-data/erc20s/top-tokens',
      {
        headers: {
          'X-API-Key': process.env.NEXT_PUBLIC_MORALIS_API_KEY,
        },
      },
    );
    if (!response.data || !Array.isArray(response.data)) {
      throw new Error('Invalid response structure');
    }
    const processedData: TokenInterface[] = response.data.map((token: any) => {
      const tempToken = {
        blockchain: 'ethereum',
        address: token.contract_address || '',
        name: token.token_name,
        symbol: token.token_symbol,
      };
      const tokenWithId = generateTokenId(tempToken);
      return {
        id: tokenWithId.id,
        name: token.token_name,
        symbol: token.token_symbol,
        address: token.contract_address || '',
        blockchain: 'ethereum',
        blockchainDisplayName: 'Ethereum',
        priceInUsd: parseFloat(token.price_usd) || 0,
        marketCapRank: parseInt(token.rank, 10) || undefined,
        marketCapInUsd: parseFloat(token.market_cap_usd) || undefined,
        logoUri: token.token_logo || undefined,
        isNative: false,
      };
    });
    return processedData;
  } catch (error) {
    logError(
      'Failed to fetch top ERC-20 tokens data from Moralis',
      'MoralisService.fetchTopErc20TokensData',
      { error },
    );
    return [];
  }
};
